import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ActivityChart } from '@/components/charts/ActivityChart';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  LoadingBlock,
  PageHeader,
  SegmentedControl,
  StatTile,
  StatusView,
} from '@/components/ui';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  formatDuration,
  formatMode,
  formatSessionDateTimeShort,
} from '@/lib/formatting';
import { buildActivitySeries, computeSessionStats, useSessions } from '../hooks';
import { effectiveDurationSec, sessionTimestamp, titleFor } from '../model';

import styles from './OverviewPage.module.css';

type Range = '30' | '90' | '365';

const rangeOptions = [
  { value: '30' as const, label: '30 days' },
  { value: '90' as const, label: '90 days' },
  { value: '365' as const, label: '1 year' },
];

const metricOptions = [
  { value: 'sessions' as const, label: 'Sessions' },
  { value: 'minutes' as const, label: 'Minutes' },
];

export function OverviewPage() {
  const { user } = useAuth();
  const { sessions, isPending, isError, error, refetch, isFetching } = useSessions();
  const navigate = useNavigate();
  const locale = navigator.language.split('-')[0] ?? 'en';

  const [range, setRange] = useState<Range>('90');
  const [metric, setMetric] = useState<'sessions' | 'minutes'>('sessions');

  const stats = useMemo(() => computeSessionStats(sessions), [sessions]);
  const activity = useMemo(
    () => buildActivitySeries(sessions, Number.parseInt(range, 10)),
    [sessions, range],
  );
  const recent = useMemo(() => sessions.slice(0, 6), [sessions]);
  const maxModeCount = stats.modes[0]?.count ?? 1;

  const firstName = user?.name?.trim().split(/\s+/)[0];

  if (isError) {
    return (
      <>
        <PageHeader eyebrow="Workspace" title="Overview" />
        <Card>
          <StatusView
            icon="cloud-off"
            tone="danger"
            title="Could not load your sessions"
            message={error instanceof Error ? error.message : 'Please try again.'}
            action={
              <Button variant="primary" icon="refresh" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title={firstName ? `Welcome back, ${firstName}` : 'Overview'}
        description="Your recording activity, at a glance. Numbers are computed from every session on your account."
        actions={
          <Button icon="refresh" busy={isFetching && !isPending} onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      />

      <div className={styles.stats}>
        <StatTile
          label="Sessions"
          icon="list"
          loading={isPending}
          value={stats.total}
          caption={
            stats.lastSession
              ? `Last on ${formatSessionDateTimeShort(stats.lastSession)}`
              : 'No dated sessions yet'
          }
        />
        <StatTile
          label="Time practised"
          icon="clock"
          loading={isPending}
          value={formatDuration(stats.totalDurationSec)}
          caption={
            stats.meanDurationSec !== null
              ? `${formatDuration(stats.meanDurationSec)} on average`
              : 'Duration unavailable'
          }
        />
        <StatTile
          label="Mean score"
          icon="activity"
          loading={isPending}
          mono
          value={stats.meanScore !== null ? stats.meanScore.toFixed(2) : '—'}
          caption={stats.bestScore !== null ? `Peak ${stats.bestScore.toFixed(2)}` : 'Not reported'}
        />
        <StatTile
          label="Raw EEG recordings"
          icon="waves"
          loading={isPending}
          value={stats.withRawData}
          caption={
            stats.total > 0
              ? `${Math.round((stats.withRawData / stats.total) * 100)}% of sessions`
              : 'None yet'
          }
        />
      </div>

      <div className={styles.grid}>
        <div className={styles.column}>
        <Card>
          <CardHeader
            title="Practice over time"
            subtitle={metric === 'sessions' ? 'Sessions recorded per day' : 'Minutes recorded per day'}
            actions={
              <>
                <SegmentedControl
                  label="Metric"
                  value={metric}
                  options={metricOptions}
                  onChange={setMetric}
                />
                <SegmentedControl
                  label="Time range"
                  value={range}
                  options={rangeOptions}
                  onChange={setRange}
                />
              </>
            }
          />
          <CardBody className={styles.chartBody}>
            {isPending ? (
              <LoadingBlock label="Loading activity…" />
            ) : sessions.length === 0 ? (
              <StatusView
                icon="waves"
                compact
                title="No sessions yet"
                message="Sessions recorded with your headband will appear here."
              />
            ) : (
              <ActivityChart data={activity} metric={metric} height={230} />
            )}
          </CardBody>
        </Card>

        {stats.modes.length > 0 ? (
          <Card>
            <CardHeader title="Sessions by mode" subtitle="How your practice is distributed" />
            <CardBody>
              <div className={styles.modeList}>
                {stats.modes.slice(0, 6).map((entry) => (
                  <div key={entry.mode} className={styles.modeRow}>
                    <span className={styles.modeName}>{formatMode(entry.mode)}</span>
                    <span className={styles.modeBarTrack}>
                      <span
                        className={styles.modeBar}
                        style={{ width: `${Math.max(4, (entry.count / maxModeCount) * 100)}%` }}
                      />
                    </span>
                    <span className={styles.modeCount}>{entry.count}</span>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        ) : null}

        </div>

        <div className={styles.column}>
          <Card>
            <CardHeader title="Recent sessions" subtitle="Your six most recent recordings" />
            {isPending ? (
              <LoadingBlock label="Loading…" />
            ) : recent.length === 0 ? (
              <StatusView icon="list" compact title="Nothing recorded yet" />
            ) : (
              <>
                <div className={styles.recent}>
                  {recent.map((session) => (
                    <button
                      key={session.id}
                      type="button"
                      className={styles.recentItem}
                      onClick={() => navigate(`/sessions/${session.id}`)}
                    >
                      <div className={styles.recentBody}>
                        <div className={styles.recentTitle}>{titleFor(session, locale)}</div>
                        <div className={styles.recentMeta}>
                          {formatSessionDateTimeShort(sessionTimestamp(session))}
                        </div>
                      </div>
                      <span className={styles.recentValue}>
                        {formatDuration(effectiveDurationSec(session)).replace(
                          'Duration unavailable',
                          '—',
                        )}
                      </span>
                      <Icon name="chevron-right" size={15} />
                    </button>
                  ))}
                </div>
                <Link to="/sessions" className={styles.footerLink}>
                  View all sessions
                  <Icon name="arrow-right" size={14} />
                </Link>
              </>
            )}
          </Card>

        </div>
      </div>
    </>
  );
}
